/**
 * Outil « Série statistique » (Boîte à outils, 5e → 1re).
 *
 * Des valeurs (des notes sur 20) en diagramme en points : on les fait glisser
 * au doigt ou aux boutons, on en ajoute, on en retire. En direct : moyenne
 * (point d'équilibre, fraction exacte), médiane (rang du milieu dans la série
 * triée), étendue ; dès la 2nde, quartiles, boîte à moustaches et écart type.
 * La médiane résiste à une valeur extrême, la moyenne non : l'outil le montre.
 * Calculs : `stats.ts`.
 */

import { useRef, useState, type PointerEvent } from 'react';
import { schoolLevelRank } from '@jeraisonne/db/cards';
import { MathText, cn } from '@jeraisonne/ui';
import { SendToSheetButton } from '../../send-to-sheet-button';
import type { ToolProps } from '../../tool-types';
import type { ExactNumber } from '../../ui/exact';
import { Note, SECONDARY_BUTTON, Segmented, Stepper, ToggleChip } from '../../ui/controls';
import { formatNumber, numberAtoms } from '../../ui/format';
import { AXIS, FIGURE_BG_CLASS, GRID, HaloText } from '../../ui/svg-parts';
import {
  MAX_COUNT,
  MAX_VALUE,
  MIN_VALUE,
  PRESETS,
  mean,
  median,
  medianRanks,
  outliers,
  quartiles,
  range,
  sorted,
  stackLevels,
  standardDeviation,
  sumText,
} from './stats';

const W = 320;
const AXIS_Y = 186;
/** sous l'axe : graduations, puis le triangle de la moyenne et son libellé */
const H = AXIS_Y + 52;
const LEFT = 18;
const RIGHT = W - 18;
const DOT = 'var(--color-primary-500)';
const MEAN = 'var(--color-accent-700)';
const MEDIAN = 'var(--color-success-600)';
const BOX = 'var(--color-ink-700)';

const xOf = (v: number) => LEFT + ((v - MIN_VALUE) / (MAX_VALUE - MIN_VALUE)) * (RIGHT - LEFT);
const vOf = (x: number) => MIN_VALUE + ((x - LEFT) / (RIGHT - LEFT)) * (MAX_VALUE - MIN_VALUE);
const tx = (s: string) => s.replace(/,/g, '{,}');
const ord = (k: number) => (k === 1 ? '1re' : `${k}e`);
const clampX = (x: number, half: number) => Math.min(RIGHT - half, Math.max(LEFT + half, x));

export function StatsSeriesTool({
  onSend,
  sendCost,
  level,
}: ToolProps & { level?: string | null }) {
  const rank = schoolLevelRank(level);
  const showMedian = rank === null || rank >= 3;
  const showRange = rank === null || rank >= 4;
  const lycee = rank === null || rank >= 5;
  const [preset, setPreset] = useState('notes');
  const [values, setValues] = useState<number[]>([...PRESETS[0]!.values]);
  const [selected, setSelected] = useState<number | null>(null);
  const [showSd, setShowSd] = useState(false);
  const svgRef = useRef<SVGSVGElement>(null);
  const dragging = useRef<number | null>(null);

  const n = values.length;
  const m = mean(values);
  const me = median(values);
  const s = sorted(values);
  /** indices des valeurs dans l'ordre croissant (série triée cliquable) */
  const order = values.map((_, i) => i).sort((a, b) => values[a]! - values[b]! || a - b);
  const q = quartiles(values);
  const sd = standardDeviation(values);
  const sdExact = Math.abs(sd - Math.round(sd * 100) / 100) < 1e-9;
  const levels = stackLevels(values);
  const maxLevel = Math.max(0, ...levels);
  // empilement : resserré quand une valeur revient souvent, pour rester dans la figure
  const top = lycee ? 44 : 14;
  const dy = maxLevel === 0 ? 16 : Math.min(16, (AXIS_Y - 12 - top - 26) / maxLevel);
  const cyOf = (level: number) => AXIS_Y - 12 - level * dy;
  const sum = values.reduce((a, b) => a + b, 0);
  const meanSymbol = lycee ? '\\bar{x}' : '\\text{Moyenne}';
  const medianSymbol = lycee ? '\\text{Me}' : '\\text{Médiane}';

  const choose = (id: string) => {
    setPreset(id);
    setValues([...PRESETS.find((p) => p.id === id)!.values]);
    setSelected(null);
  };
  const setValue = (i: number, v: number) => {
    setPreset('');
    setValues((old) =>
      old.map((x, j) => (j === i ? Math.min(MAX_VALUE, Math.max(MIN_VALUE, Math.round(v))) : x)),
    );
  };

  const toView = (e: PointerEvent<SVGSVGElement>) => {
    const r = e.currentTarget.getBoundingClientRect();
    if (!r.width || !r.height) return null;
    return { x: ((e.clientX - r.left) / r.width) * W, y: ((e.clientY - r.top) / r.height) * H };
  };
  const onDown = (e: PointerEvent<SVGSVGElement>) => {
    const p = toView(e);
    if (!p) return;
    let best: number | null = null;
    let bestD = 24;
    values.forEach((v, i) => {
      const d = Math.hypot(xOf(v) - p.x, cyOf(levels[i]!) - p.y);
      if (d < bestD) {
        best = i;
        bestD = d;
      }
    });
    if (best === null) return;
    e.currentTarget.setPointerCapture?.(e.pointerId);
    dragging.current = best;
    setSelected(best);
  };
  const onMove = (e: PointerEvent<SVGSVGElement>) => {
    if (dragging.current === null) return;
    const p = toView(e);
    if (p) setValue(dragging.current, vOf(p.x));
  };
  const onUp = () => {
    dragging.current = null;
  };

  const gap = Math.abs(m.value - me.value);
  const far = [...new Set(outliers(values))].sort((a, b) => a - b);

  return (
    <div className="space-y-4">
      <Segmented
        ariaLabel="Série"
        value={preset}
        options={PRESETS.map((p) => ({ value: p.id, label: p.label }))}
        onChange={choose}
      />
      <p className="text-ink-600 text-sm">
        Fais glisser un point, ou touche-le puis règle sa valeur. Les indicateurs suivent.
      </p>

      <div className="mx-auto w-full max-w-[26rem]">
        <svg
          ref={svgRef}
          viewBox={`0 0 ${W} ${H}`}
          className={cn(
            'border-ink-100 block w-full touch-pan-y select-none rounded-2xl border',
            FIGURE_BG_CLASS,
          )}
          role="img"
          aria-label={`Diagramme en points de ${n} valeurs entre ${MIN_VALUE} et ${MAX_VALUE}.`}
          data-testid="ss-figure"
          onPointerDown={onDown}
          onPointerMove={onMove}
          onPointerUp={onUp}
          onPointerCancel={onUp}
        >
          {Array.from({ length: 21 }, (_, v) => (
            <g key={v}>
              {v % 5 === 0 && <line x1={xOf(v)} x2={xOf(v)} y1={8} y2={AXIS_Y} stroke={GRID} />}
              <line
                x1={xOf(v)}
                x2={xOf(v)}
                y1={AXIS_Y}
                y2={AXIS_Y + (v % 5 === 0 ? 6 : 3)}
                stroke={AXIS}
              />
              {v % 5 === 0 && (
                <text
                  x={xOf(v)}
                  y={AXIS_Y + 18}
                  textAnchor="middle"
                  fontSize={12}
                  fill="var(--color-ink-500)"
                >
                  {v}
                </text>
              )}
            </g>
          ))}
          <line x1={LEFT} x2={RIGHT} y1={AXIS_Y} y2={AXIS_Y} stroke={AXIS} strokeWidth={1.5} />
          {lycee && (
            <g data-testid="ss-box">
              {showSd && (
                <rect
                  x={xOf(Math.max(MIN_VALUE, m.value - sd))}
                  y={8}
                  width={
                    xOf(Math.min(MAX_VALUE, m.value + sd)) - xOf(Math.max(MIN_VALUE, m.value - sd))
                  }
                  height={AXIS_Y - 8}
                  fill={MEAN}
                  fillOpacity={0.08}
                  data-testid="ss-sd-band"
                />
              )}
              <line x1={xOf(s[0]!)} x2={xOf(q.q1)} y1={26} y2={26} stroke={BOX} strokeWidth={1.5} />
              <line
                x1={xOf(q.q3)}
                x2={xOf(s[n - 1]!)}
                y1={26}
                y2={26}
                stroke={BOX}
                strokeWidth={1.5}
              />
              <line
                x1={xOf(s[0]!)}
                x2={xOf(s[0]!)}
                y1={20}
                y2={32}
                stroke={BOX}
                strokeWidth={1.5}
              />
              <line
                x1={xOf(s[n - 1]!)}
                x2={xOf(s[n - 1]!)}
                y1={20}
                y2={32}
                stroke={BOX}
                strokeWidth={1.5}
              />
              <rect
                x={xOf(q.q1)}
                y={16}
                width={Math.max(1, xOf(q.q3) - xOf(q.q1))}
                height={20}
                fill="var(--fig-bg, #fff)"
                stroke={BOX}
                strokeWidth={1.5}
              />
              <line
                x1={xOf(me.value)}
                x2={xOf(me.value)}
                y1={16}
                y2={36}
                stroke={MEDIAN}
                strokeWidth={2.5}
              />
            </g>
          )}
          {showMedian && (
            <line
              x1={xOf(me.value)}
              x2={xOf(me.value)}
              y1={cyOf(maxLevel) - 12}
              y2={AXIS_Y}
              stroke={MEDIAN}
              strokeDasharray="3 3"
              strokeOpacity={0.7}
            />
          )}
          {values.map((v, i) => (
            <circle
              key={i}
              cx={xOf(v)}
              cy={cyOf(levels[i]!)}
              r={selected === i ? 8 : 7}
              fill={DOT}
              fillOpacity={selected === i ? 1 : 0.75}
              stroke={selected === i ? 'var(--color-ink-900)' : 'none'}
              strokeWidth={2}
            />
          ))}
          {showMedian && (
            <path
              d={`M${xOf(me.value)},${cyOf(maxLevel) - 12} l6,-7 l-6,-7 l-6,7 z`}
              fill={MEDIAN}
              data-testid="ss-median-mark"
            />
          )}
          {/* la moyenne : le point d'équilibre, sous les graduations */}
          <path d={`M${xOf(m.value)},${AXIS_Y + 24} l7,11 l-14,0 z`} fill={MEAN} />
          <HaloText
            x={clampX(xOf(m.value), 28)}
            y={AXIS_Y + 49}
            textAnchor="middle"
            fontSize={12}
            fontWeight={700}
            fill={MEAN}
          >
            moyenne
          </HaloText>
        </svg>
        {showMedian && (
          <p className="mt-1 flex flex-wrap justify-center gap-x-4 text-xs font-semibold">
            <span className="text-success-600">◆ médiane</span>
            <span className="text-accent-700">▲ moyenne</span>
            {lycee && <span className="text-ink-600">boîte : Q1, Me, Q3</span>}
          </p>
        )}
      </div>

      <div className="flex flex-wrap items-center gap-2">
        {selected !== null && selected < n ? (
          <div className="min-w-0 flex-1">
            <Stepper
              label="Valeur choisie"
              value={values[selected]!}
              min={MIN_VALUE}
              max={MAX_VALUE}
              onChange={(v) => setValue(selected, v)}
            />
          </div>
        ) : (
          <p className="text-ink-500 min-w-0 flex-1 text-sm">
            Touche un point, ou une valeur de la série triée, pour la régler.
          </p>
        )}
      </div>
      <div className="flex flex-wrap gap-2">
        <button
          type="button"
          className={cn(SECONDARY_BUTTON, 'disabled:opacity-40')}
          disabled={n >= MAX_COUNT}
          onClick={() => {
            setPreset('');
            setValues((old) => [...old, 10]);
            setSelected(n);
          }}
        >
          Ajouter une valeur
        </button>
        <button
          type="button"
          className={cn(SECONDARY_BUTTON, 'disabled:opacity-40')}
          disabled={selected === null || n <= 2}
          onClick={() => {
            setPreset('');
            setValues((old) => old.filter((_, j) => j !== selected));
            setSelected(null);
          }}
        >
          Retirer la valeur choisie
        </button>
      </div>

      <section aria-label="Série triée" className="space-y-1">
        <p className="text-ink-500 text-xs font-semibold uppercase tracking-wide">
          Série triée ({n} valeur{n > 1 ? 's' : ''})
        </p>
        <ol className="flex flex-wrap gap-1" data-testid="ss-sorted">
          {order.map((idx, k) => {
            const v = values[idx]!;
            const r = k + 1;
            const isMed = showMedian && medianRanks(n).includes(r);
            const isQ = lycee && (r === q.r1 || r === q.r3);
            const role = [
              isMed ? (n % 2 === 1 ? 'médiane' : 'sert au calcul de la médiane') : null,
              isQ ? (r === q.r1 ? 'Q1' : 'Q3') : null,
            ].filter(Boolean);
            return (
              <li key={idx}>
                <button
                  type="button"
                  aria-pressed={selected === idx}
                  aria-label={`rang ${r} : ${v}${role.length ? ` (${role.join(', ')})` : ''}`}
                  onClick={() => setSelected(idx)}
                  className={cn(
                    'min-h-11 min-w-11 rounded-lg px-2 text-center text-sm tabular-nums',
                    isMed
                      ? 'bg-success-50 text-ink-900 font-bold'
                      : isQ
                        ? 'bg-ink-100 text-ink-900 font-semibold'
                        : 'text-ink-700',
                    selected === idx && 'ring-ink-900 ring-2',
                  )}
                >
                  {v}
                </button>
              </li>
            );
          })}
        </ol>
      </section>

      <section
        className="border-ink-100 space-y-3 rounded-2xl border px-3 py-3"
        aria-label="Indicateurs"
        data-testid="ss-results"
      >
        <Indicator
          tex={`${meanSymbol} = \\frac{${sumText(values)}}{${n}} = \\frac{${sum}}{${n}}${!m.plain.includes('/') ? ` = ${m.tex}` : m.plain === `${sum}/${n}` ? ` \\approx ${tx(formatNumber(m.value, 2))}` : ` = ${m.tex} \\approx ${tx(formatNumber(m.value, 2))}`}`}
          caption="Moyenne : la somme partagée également entre les valeurs — le point d’équilibre du diagramme."
          exact={m}
          onSend={onSend}
          sendCost={sendCost}
          testId="ss-mean"
        />
        {showMedian && (
          <Indicator
            tex={`${medianSymbol} = ${me.tex}`}
            caption={
              n % 2 === 1
                ? `Médiane : la ${ord((n + 1) / 2)} des ${n} valeurs triées — autant de valeurs de chaque côté.`
                : `Médiane : la moyenne des ${ord(n / 2)} et ${ord(n / 2 + 1)} des ${n} valeurs triées.`
            }
            exact={me}
            onSend={onSend}
            sendCost={sendCost}
            testId="ss-median"
          />
        )}
        {showRange && (
          <Indicator
            tex={`\\text{étendue} = ${s[n - 1]} - ${s[0]} = ${range(values)}`}
            exact={null}
            onSend={onSend}
            sendCost={sendCost}
            sendValue={range(values)}
          />
        )}
        {lycee && (
          <Indicator
            tex={`Q_1 = ${q.q1}, \\quad Q_3 = ${q.q3}`}
            caption={`Q1 : la ${ord(q.r1)} valeur triée (au moins 25 % des valeurs sont inférieures ou égales) ; Q3 : la ${ord(q.r3)} (au moins 75 %).`}
            exact={null}
            onSend={onSend}
            sendCost={sendCost}
            testId="ss-quartiles"
          />
        )}
        {lycee && (
          <Indicator
            tex={`\\text{écart interquartile} = Q_3 - Q_1 = ${q.q3 - q.q1}`}
            caption="La largeur de la boîte : au moins la moitié des valeurs y sont."
            exact={null}
            onSend={onSend}
            sendCost={sendCost}
            sendValue={q.q3 - q.q1}
            testId="ss-iqr"
          />
        )}
        {lycee && (
          <div className="space-y-1">
            <Indicator
              tex={`\\sigma = \\sqrt{\\frac{1}{n}\\sum (x_i - \\bar{x})^2} ${sdExact ? '=' : '\\approx'} ${tx(formatNumber(sd, 2))}`}
              caption="Écart type : l’écart moyen (quadratique) des valeurs à la moyenne."
              exact={null}
              onSend={onSend}
              sendCost={sendCost}
              sendValue={sdExact ? Number(sd.toFixed(2)) : undefined}
              testId="ss-sd"
            />
            <ToggleChip on={showSd} onClick={() => setShowSd((o) => !o)}>
              Montrer [x̄ − σ ; x̄ + σ]
            </ToggleChip>
          </div>
        )}
      </section>

      {showMedian && gap >= 1 && (
        <Note live={false}>
          {far.length > 0
            ? `Moyenne et médiane s’écartent de ${formatNumber(gap, 2)} : ${
                far.length === 1
                  ? `la valeur extrême ${far[0]} tire la moyenne`
                  : `les valeurs extrêmes ${far.slice(0, -1).join(', ')} et ${far[far.length - 1]} tirent la moyenne`
              }, la médiane résiste. Fais-la glisser vers les autres pour le voir.`
            : `Moyenne et médiane s’écartent de ${formatNumber(gap, 2)} : la série est dissymétrique. Les ${
                m.value > me.value ? 'grandes' : 'petites'
              } valeurs sont plus étalées et tirent la moyenne de leur côté.`}
        </Note>
      )}
    </div>
  );
}

function Indicator({
  tex,
  caption,
  exact,
  sendValue,
  onSend,
  sendCost,
  testId,
}: {
  tex: string;
  caption?: string;
  exact: ExactNumber | null;
  sendValue?: number;
  onSend?: ToolProps['onSend'];
  sendCost: number;
  testId?: string;
}) {
  const label = exact ? exact.plain : sendValue !== undefined ? formatNumber(sendValue, 2) : null;
  return (
    <div className="space-y-1" data-testid={testId}>
      <MathText className="text-ink-900 block overflow-x-auto text-base">{`$${tex}$`}</MathText>
      <div className="flex flex-wrap items-center gap-2">
        {caption && <p className="text-ink-600 min-w-0 flex-1 text-sm">{caption}</p>}
        {onSend && label && (
          <SendToSheetButton
            label={label}
            cost={sendCost}
            onClick={() => onSend({ atoms: exact ? exact.atoms : numberAtoms(sendValue!, 2) })}
          />
        )}
      </div>
    </div>
  );
}
